import React, {createContext, PropsWithChildren, useContext, useEffect, useState} from "react";
import {env} from "@bounce/shared/constants";

export interface ActivityEventContextProps {
  isUserInactive: boolean;
  setIsUserInactive: (value: boolean) => void;
  stopUserInactivityTooltip: boolean;
  setStopUserInactivityTooltip: (value: boolean) => void;
  onStopUserInactivityTooltip: () => void;
  isPlansTooltipActive: boolean;
  setIsPlansTooltipActive: (value: boolean) => void;
}

const ActivityEventContext = createContext<ActivityEventContextProps>(undefined!);

const ActivityEventProvider = ({children}: PropsWithChildren) => {
  const [isUserInactive, setIsUserInactive] = useState(false);
  const [stopUserInactivityTooltip, setStopUserInactivityTooltip] = useState(false);
  const [isPlansTooltipActive, setIsPlansTooltipActive] = useState(false);

  const onStopUserInactivityTooltip = () => {
    setIsUserInactive(false);
    setStopUserInactivityTooltip(true);
  };

  useEffect(() => {
    let inactivityTimeout: any;
    if (["production"].includes(env)) {
      const resetInactivityTimeout = () => {
        clearTimeout(inactivityTimeout);
        inactivityTimeout = setTimeout(() => {
          setIsUserInactive(true);
        }, 10000);
      };
      const handleActivity = () => {
        resetInactivityTimeout();
      };
      window.addEventListener("mousemove", handleActivity);
      window.addEventListener("keydown", handleActivity);
      resetInactivityTimeout();
      return () => {
        window.removeEventListener("mousemove", handleActivity);
        window.removeEventListener("keydown", handleActivity);
      };
    }
  }, []);

  return (
    <ActivityEventContext.Provider
      value={{
        isUserInactive,
        setIsUserInactive,
        stopUserInactivityTooltip,
        setStopUserInactivityTooltip,
        onStopUserInactivityTooltip,
        isPlansTooltipActive,
        setIsPlansTooltipActive,
      }}
    >
      {children}
    </ActivityEventContext.Provider>
  );
};

const useActivityEvent = (): ActivityEventContextProps => {
  const context = useContext(ActivityEventContext);

  if (!context) throw new Error("ActivityEventContext must be used inside ActivityEventProvider");

  return context;
};

export {ActivityEventContext, ActivityEventProvider, useActivityEvent};
